(() => {
  'use strict';
  const section = document.getElementById('anatomy');
  if (!section) return;

  const stage = document.getElementById('bed-stage');
  const visual = document.getElementById('bed-visual');
  const poster = document.getElementById('bed-poster');
  const canvas = document.getElementById('bed-canvas');
  if (!canvas) return;

  const context = canvas.getContext('2d', { alpha: false });
  const M = window.BBSequenceMath;
  if (!M || !context) return;

  const header = document.getElementById('header');
  const labels = document.getElementById('bed-callouts');
  const actions = document.getElementById('bed-final-actions');
  const counter = document.getElementById('bed-frame');
  const phase = document.getElementById('bed-phase');
  const progressbar = document.getElementById('bed-progress');
  const fill = document.getElementById('bed-progress-fill');

  const desktop = matchMedia('(min-width: 1024px) and (min-height: 600px) and (hover: hover) and (pointer: fine)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  const path = section.dataset.framePath;
  const frameURL = index => `${path}${String(index + 1).padStart(3, '0')}.jpg`;
  const frames = Array(40).fill(null);
  const loading = new Set();
  const attempts = Array(40).fill(0);
  const jobs = [];

  let enabled = false;
  let failed = false;
  let generation = 0;
  let lastPainted = -1;
  let requested = 0;
  let progress = 0;
  let raf = 0;
  let near = false;
  let dirtyLayout = true;
  let didPreload = false;

  function eligible() {
    const isReduced = reduced.matches || document.body.classList.contains('reduced-motion');
    return desktop.matches && !isReduced;
  }

  function interactive(element, visible) {
    if (!element) return;
    element.inert = !visible;
    element.setAttribute('aria-hidden', String(!visible));
  }

  function showStaticFailure() {
    if (failed) return;
    failed = true;
    jobs.length = 0;
    section.classList.add('is-fallback');
    const still = new Image();
    const thisGeneration = generation;
    still.onload = () => {
      if (thisGeneration !== generation || !enabled) return;
      context.drawImage(still, M.crop.x, M.crop.y, M.crop.width, M.crop.height, 0, 0, 1280, 720);
      section.classList.add('has-painted-frame');
      lastPainted = 39;
      renderStates();
    };
    still.src = frameURL(39);
    renderStates();
  }

  async function loadFrame(index, thisGeneration) {
    attempts[index]++;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = index === requested ? 'auto' : 'low';
    try {
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = frameURL(index);
      });
      if (image.decode) {
        try { await image.decode(); } catch (err) { if (!image.naturalWidth) throw err; }
      }
      if (generation !== thisGeneration || !enabled || failed) return;
      frames[index] = image;
      if (index === requested) queueRender();
    } catch {
      if (generation === thisGeneration && enabled) {
        if (attempts[index] < 2) jobs.unshift(index);
        else showStaticFailure();
      }
    } finally {
      if (generation === thisGeneration) {
        loading.delete(index);
        pump();
      }
    }
  }

  function pump() {
    if (!enabled || failed) return;
    while (loading.size < 4 && jobs.length) {
      const index = jobs.shift();
      if (frames[index] || loading.has(index)) continue;
      loading.add(index);
      void loadFrame(index, generation);
    }
  }

  function requestFrame(index, priority = false) {
    if (frames[index] || loading.has(index) || !enabled || failed) return;
    const existing = jobs.indexOf(index);
    if (existing !== -1) {
      if (!priority) return;
      jobs.splice(existing, 1);
    }
    if (priority) jobs.unshift(index);
    else jobs.push(index);
    pump();
  }

  function preload() {
    if (!enabled || failed || didPreload) return;
    didPreload = true;
    requestFrame(requested, true);
    for (let i = 0; i < 40; i++) requestFrame(i);
  }

  function layout() {
    dirtyLayout = false;
    const headerHeight = header ? header.getBoundingClientRect().height : 94;
    section.style.setProperty('--bed-header', `${headerHeight}px`);
    if (!enabled) return;

    const r = M.composition(stage.clientWidth, stage.clientHeight, headerHeight);
    const values = { left: r.left, top: r.top, width: r.width, height: r.height };
    for (const [key, value] of Object.entries(values)) {
      section.style.setProperty(`--visual-${key}`, `${value.toFixed(2)}px`);
    }

    if (labels) {
      labels.querySelectorAll('[data-source-x]').forEach(label => {
        label.style.setProperty('--point-x', `${r.left + (Number(label.dataset.sourceX) - M.crop.x) * r.scale}px`);
        label.style.setProperty('--point-y', `${r.top + (Number(label.dataset.sourceY) - M.crop.y) * r.scale}px`);
      });
    }
  }

  function renderStates() {
    if (!enabled) return;
    const finalImageAvailable = lastPainted === 39;
    const labelsVisible = (progress >= 0.75 && finalImageAvailable) || (failed && finalImageAvailable);
    const shoppingVisible = (progress >= 0.95 && finalImageAvailable) || failed;
    const intro = failed ? 0 : 1 - M.clamp((progress - 0.16) / 0.35);

    section.style.setProperty('--intro-opacity', intro.toFixed(3));
    section.style.setProperty('--intro-shift', `${(-12 * (1 - intro)).toFixed(2)}px`);
    section.style.setProperty('--label-opacity', labelsVisible ? '1' : '0');
    section.style.setProperty('--action-opacity', shoppingVisible ? '1' : '0');

    if (labels) {
      labels.classList.toggle('is-discoverable', labelsVisible);
      interactive(labels, labelsVisible);
    }
    if (actions) {
      actions.classList.toggle('is-shoppable', shoppingVisible);
      interactive(actions, shoppingVisible);
    }

    const number = Math.max(0, lastPainted) + 1;
    if (counter) counter.textContent = String(number).padStart(2, '0');
    if (progressbar) progressbar.setAttribute('aria-valuenow', String(number));
    if (fill) fill.style.transform = `scaleX(${progress})`;

    const text = progress < 0.15
      ? '01 — A beautiful beginning'
      : progress < 0.75
      ? '02 — The layers, revealed'
      : progress < 0.95
      ? '03 — Discover your comfort'
      : '04 — Beautifully composed';
    if (phase && phase.textContent !== text) phase.textContent = text;
  }

  function render() {
    raf = 0;
    if (dirtyLayout) layout();
    if (!enabled) return;

    const bounds = section.getBoundingClientRect();
    progress = M.progressAt(window.scrollY, window.scrollY + bounds.top, section.offsetHeight, stage.clientHeight);
    requested = M.frameAt(progress);

    if (bounds.top < window.innerHeight + 1400 && bounds.bottom > -500) preload();
    if (near || (bounds.top < window.innerHeight && bounds.bottom > 0)) requestFrame(requested, true);

    if (!failed && frames[requested] && requested !== lastPainted) {
      context.drawImage(frames[requested], M.crop.x, M.crop.y, M.crop.width, M.crop.height, 0, 0, 1280, 720);
      lastPainted = requested;
      section.classList.add('has-painted-frame');
    }
    renderStates();
  }

  function queueRender() {
    if (!raf) raf = requestAnimationFrame(render);
  }

  function configure() {
    const next = eligible();
    if (next === enabled && section.dataset.initialized === 'true') {
      dirtyLayout = true;
      queueRender();
      return;
    }
    section.dataset.initialized = 'true';
    generation++;
    enabled = next;
    failed = false;
    didPreload = false;
    lastPainted = -1;
    requested = 0;
    jobs.length = 0;
    loading.clear();
    frames.fill(null);
    attempts.fill(0);

    section.classList.toggle('is-pinned', enabled);
    section.classList.remove('has-painted-frame', 'is-fallback');

    if (enabled) {
      section.style.height = `${Number(section.dataset.scrollViewports || 3.1) * 100}dvh`;
      if (poster) poster.src = frameURL(0);
      interactive(labels, false);
      interactive(actions, false);
      requestFrame(0, true);
      if (near) preload();
    } else {
      section.style.removeProperty('height');
      if (poster) poster.src = frameURL(39);
      ['--intro-opacity', '--intro-shift', '--label-opacity', '--action-opacity'].forEach(key =>
        section.style.removeProperty(key)
      );
      if (labels) {
        labels.classList.add('is-discoverable');
        interactive(labels, true);
      }
      if (actions) {
        actions.classList.add('is-shoppable');
        interactive(actions, true);
      }
    }
    dirtyLayout = true;
    queueRender();
  }

  const skipBtn = document.getElementById('bed-skip');
  if (skipBtn) {
    skipBtn.addEventListener('click', () => {
      if (!enabled) return;
      const start = window.scrollY + section.getBoundingClientRect().top;
      const distance = section.offsetHeight - stage.clientHeight;
      window.scrollTo({ top: start + distance * 0.97, behavior: 'instant' });
      progress = 0.97;
      requested = 39;
      requestFrame(39, true);
      queueRender();
    });
  }

  window.addEventListener('scroll', () => {
    if (enabled && (near || !('IntersectionObserver' in window))) queueRender();
  }, { passive: true });

  window.addEventListener('resize', () => { dirtyLayout = true; queueRender(); });
  desktop.addEventListener('change', configure);
  reduced.addEventListener('change', configure);

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(entries => {
      near = entries.some(entry => entry.isIntersecting);
      if (near) { preload(); queueRender(); }
    }, { rootMargin: '1200px 0px', threshold: 0 }).observe(section);
  }

  configure();
})();
